import { ChartAxisDirection, EventEmitter, type EventListener } from 'ag-charts-core';

import type { ISeries } from './series/seriesTypes';

export interface UpdateCompleteEvent {
    readonly type: 'update-complete';
    readonly apiUpdate: boolean;
    readonly wasShortcut: boolean;
}

export interface PreDomUpdateEvent {
    readonly type: 'pre-dom-update';
}

export interface PreSeriesUpdateEvent {
    readonly type: 'pre-series-update';
    readonly requiredRangeRatio: number;
    readonly requiredRangeDirection: ChartAxisDirection;
    readonly requiredRange: number;
}

export interface PreSceneRenderEvent {
    readonly type: 'pre-scene-render';
    readonly apiUpdate: boolean;
}

export interface ProcessDataEvent {
    readonly type: 'process-data';
    readonly series: { shouldFlipXY?: boolean };
}

export interface UpdateOpts {
    forceNodeDataRefresh?: boolean;
    skipAnimations?: boolean;
    newAnimationBatch?: boolean;
    seriesToUpdate?: Iterable<ISeries<any, any, any>>;
    backOffMs?: number;
    apiUpdate?: boolean;
    clearCallbackCache?: boolean;
}

interface EventMap {
    'update-complete': UpdateCompleteEvent;
    'pre-dom-update': PreDomUpdateEvent;
    'pre-series-update': PreSeriesUpdateEvent;
    'pre-scene-render': PreSceneRenderEvent;
    'process-data': ProcessDataEvent;
}

export class UpdateService {
    private readonly events = new EventEmitter<EventMap>();

    public addListener<K extends keyof EventMap>(eventName: K, listener: EventListener<EventMap[K]>) {
        return this.events.on(eventName, listener);
    }

    public destroy() {
        this.events.clear();
    }

    public dispatchUpdateComplete(apiUpdate: boolean, wasShortcut: boolean) {
        this.events.emit('update-complete', { type: 'update-complete', apiUpdate, wasShortcut });
    }

    public dispatchPreDomUpdate() {
        this.events.emit('pre-dom-update', { type: 'pre-dom-update' });
    }

    public dispatchPreSeriesUpdate(
        requiredRangeRatio: number,
        requiredRangeDirection: ChartAxisDirection,
        requiredRange: number
    ) {
        this.events.emit('pre-series-update', {
            type: 'pre-series-update',
            requiredRangeRatio,
            requiredRangeDirection,
            requiredRange,
        });
    }

    public dispatchPreSceneRender(apiUpdate: boolean) {
        this.events.emit('pre-scene-render', { type: 'pre-scene-render', apiUpdate });
    }

    public dispatchProcessData({ series }: { series: { shouldFlipXY?: boolean } }) {
        this.events.emit('process-data', { type: 'process-data', series });
    }
}
